import { useState, useEffect, useContext } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttDialog,
    SttFormSelect,
    SttFormPrice,
    SttSelectPForm,
    SttFormTextarea,
} from '../../sporttia/all';
import {
    formatPriceByLocale,
    formatPriceForApi,
    parsePriceToNumber,
} from '../../lib/utils';
import constants from '../../config/constants';
import DataphonePaymentDialog from './DataphonePaymentDialog';
import POSStripePaymentDialog from '../stripe/POSStripePaymentDialog';

type Mship = {
    id: number;
};

type Deposit = {
    concept: string;
    paymentForm: string;
    chargingOption: number | null;
    price: string | number;
};

type ChargingOption = {
    id: number;
    amount: number;
    bonus?: number;
    amountShape?: {
        currency?: string;
        locale?: string;
    };
    bonusShape?: {
        price?: number;
        currency?: string;
        locale?: string;
    };
};

type ChargingOptionsResponse = {
    rows: ChargingOption[];
};

type PaymentResponse = {
    payment?: {
        price: number;
        concept: string;
        paymentForm?: string;
        tpv?: {
            id: number;
        };
    };
};

type LocalPOSPaymentParams = {
    amount: number;
    concept: string;
    idTpv: number;
    sc: {
        id?: number;
        scName?: string;
        entityName?: string;
        address?: string;
        cif?: string;
        phone?: string;
    };
    returnParams: unknown;
} | null;

type ChargePurseDialogProps = {
    mship: Mship | null;
    onClose?: () => void;
    onSuccess?: () => void;
};

type FormChangeEvent = {
    name: string | undefined;
    value: unknown;
};

type PurseAction = 'Deposit' | 'Return';

export default function ChargePurseDialog({
    mship,
    onClose,
    onSuccess,
}: ChargePurseDialogProps): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const [deposit, setDeposit] = useState<Deposit>({
        concept: '',
        paymentForm: 'CASH',
        chargingOption: null,
        price: '',
    });
    const [chargingOptions, setChargingOptions] = useState<ChargingOption[]>(
        [],
    );
    const [posPaymentParams, setPosPaymentParams] =
        useState<LocalPOSPaymentParams>(null);
    const [purseAction, setPurseAction] = useState<PurseAction>('Deposit');
    const paymentMethods = ['CASH', 'BANK', 'DATAFONO', 'FREE', ''];
    const returnPaymentMethods = ['CASH', 'BANK'];

    if (cxt?.sc?.dataphoneConnected) {
        paymentMethods.push(
            constants.payment.paymentForms.dataphoneConnected.name,
        );
    }

    /**
     * Load charging options
     */
    const loadChargingOptions = (): void => {
        if (!cxt?.sc?.id) return;

        cxt.api('GET', `/scs/${cxt.sc?.id}/chargings/options`, {
            params: { page: 1, rows: 100 },
            success: (r: ChargingOptionsResponse) => {
                setChargingOptions(r.rows);
            },
        });
    };

    /**
     * Add deposit
     */
    const addDeposit = (): void => {
        if (!mship?.id) return;

        // The amount field emits the raw typed text, so the decimal
        // separator can be a comma ("5,45") or a dot ("5.45").
        const amount = parsePriceToNumber(deposit.price) ?? 0;

        cxt?.api('POST', `/mships/${mship.id}/purses/deposits`, {
            params: {
                concept: deposit.concept,
                paymentForm: deposit.paymentForm,
                price: formatPriceForApi(
                    purseAction === 'Return' ? -amount : amount,
                ),
            },
            success: (response: PaymentResponse) => {
                cxt.showMessage('S', t('page.sc.user.chargeSuccess'));
                if (
                    response?.payment?.tpv &&
                    response?.payment?.paymentForm?.toUpperCase() ===
                        'DATAFONO_CONNECTED'
                ) {
                    setPosPaymentParams({
                        amount: response.payment.price,
                        concept: response.payment.concept,
                        idTpv: response.payment.tpv.id,
                        sc: {
                            id: cxt?.sc?.id,
                            scName: cxt?.sc?.name,
                        },
                        returnParams: null,
                    });
                    return;
                }
                onClose?.();
                onSuccess?.();
            },
        });
    };

    /**
     * Handle form
     */
    const handleForm = ({ name, value }: FormChangeEvent): void => {
        if (!name) return;
        // Charging option changes
        let option: ChargingOption | undefined;
        if (name === 'chargingOption') {
            option = chargingOptions.find((e: any) => e.id === value);
        }

        const newDeposit = { ...deposit, [name]: value };
        if (option) {
            newDeposit.price = option.amount;
        }
        setDeposit(newDeposit);
    };

    /**
     * Init
     */
    useEffect(() => {
        loadChargingOptions();
    }, []);

    // -----| Render |-----
    return (
        <SttDialog
            title={t('NewPurseMovement')}
            open={mship !== null && mship !== undefined}
            onClose={() => onClose?.()}
            maxWidth="md"
            content={
                <Grid container spacing={3}>
                    <SttFormTextarea
                        grid
                        name="concept"
                        caption={t('PursePayment')}
                        defVal={deposit.concept}
                        onChange={handleForm}
                    />

                    <SttFormSelect
                        grid
                        md={2}
                        sm={4}
                        name="option"
                        defVal={purseAction}
                        onChange={({
                            value,
                        }: {
                            name: string | undefined;
                            value: unknown;
                        }) => {
                            setPurseAction(value as PurseAction);
                            setDeposit({ ...deposit, paymentForm: 'CASH' });
                        }}
                        options={[
                            { caption: t('Deposit'), value: 'Deposit' },
                            { caption: t('Return'), value: 'Return' },
                        ]}
                    />

                    <SttSelectPForm
                        grid
                        md={3}
                        sm={4}
                        name="paymentForm"
                        caption={t('PaymentForm')}
                        defVal={deposit.paymentForm}
                        onChange={handleForm}
                        activePFs={
                            purseAction === 'Deposit'
                                ? paymentMethods
                                : returnPaymentMethods
                        }
                    />

                    {purseAction === 'Deposit' && (
                        <SttFormSelect
                            grid
                            md={4}
                            sm={8}
                            name="chargingOption"
                            caption={t('SelectCharge')}
                            defVal={deposit.chargingOption}
                            onChange={handleForm}
                            options={chargingOptions.map((opt: any) => ({
                                caption: `${t('Amount')}: ${formatPriceByLocale(
                                    opt?.amount,
                                    opt?.amountShape?.currency,
                                    opt?.amountShape?.locale,
                                )}${
                                    opt.bonus
                                        ? `, bonus: ${formatPriceByLocale(
                                              opt?.bonusShape?.price,
                                              opt?.bonusShape?.currency,
                                              opt?.bonusShape?.locale,
                                          )}`
                                        : ''
                                }`,
                                value: opt.id,
                            }))}
                        />
                    )}
                    <SttFormPrice
                        grid
                        md={3}
                        sm={4}
                        name="price"
                        disabled={deposit.chargingOption !== null}
                        caption={t('Amount')}
                        defVal={String(deposit.price)}
                        onChange={handleForm}
                    />
                    {cxt?.sc?.sporttiaStripeLocationId &&
                    posPaymentParams !== null ? (
                        <POSStripePaymentDialog
                            paymentData={posPaymentParams}
                            onClose={() => setPosPaymentParams(null)}
                            onSuccess={() => {
                                onSuccess?.();
                                onClose?.();
                            }}
                        />
                    ) : (
                        <DataphonePaymentDialog
                            open={posPaymentParams !== null}
                            paymentData={posPaymentParams}
                            onClose={() => setPosPaymentParams(null)}
                            onSuccess={() => {
                                onSuccess?.();
                                onClose?.();
                            }}
                        />
                    )}
                </Grid>
            }
            buttons={[
                { type: 'accept', onClick: () => addDeposit() },
                { type: 'close', onClick: () => onClose?.() },
            ]}
        />
    );
}
